import { isAxiosError } from "axios";
import { getRequestValidationMessage } from "@lib/backendClient";

interface ApiErrorBody {
    message?: string | string[];
}

/** Returns the `message` string from a ts-rest error body when present, otherwise the fallback. */
export function extractApiErrorMessage<T>(body: T, fallback: string): string {
    if (
        body &&
        typeof body === "object" &&
        "message" in body &&
        typeof body.message === "string"
    ) {
        return body.message;
    }
    return fallback;
}

/** HTTP status of a request the backend client rejected, if it reached the server. */
export function getApiErrorStatus(error: unknown): number | undefined {
    return isAxiosError(error) ? error.response?.status : undefined;
}

const HTTP_CONFLICT = 409;

/** True when the server rejected the request with 409 Conflict. */
export function isConflictError(error: unknown): boolean {
    return getApiErrorStatus(error) === HTTP_CONFLICT;
}

const DEFAULT_MAX_QUERY_RETRIES = 3;

/** React Query `retry` option: gives up at once on the given statuses, otherwise retries up to 3 times. */
export function retryUnlessStatus(
    statuses: readonly number[],
): (failureCount: number, error: unknown) => boolean {
    return (failureCount, error) => {
        const status = getApiErrorStatus(error);
        if (status !== undefined && statuses.includes(status)) {
            return false;
        }
        return failureCount < DEFAULT_MAX_QUERY_RETRIES;
    };
}

/** True when the server answered at all; the backend client toasts those itself. */
export function didRequestReachServer(error: unknown): boolean {
    return isAxiosError(error) && error.response !== undefined;
}

/** Server `message` of a rejected request, else the error's own message, else the fallback. */
export function getApiErrorMessage(error: unknown, fallback: string): string {
    if (isAxiosError(error)) {
        return extractApiErrorMessage(error.response?.data, fallback);
    }
    return error instanceof Error ? error.message : fallback;
}

/** Server reason of a rejected request: its `message` (the first, when a list) or its first request-validation issue. */
export function findApiErrorMessage(error: Error): string | null {
    if (!isAxiosError<ApiErrorBody>(error)) {
        return null;
    }
    const body = error.response?.data;
    const message = Array.isArray(body?.message)
        ? body.message[0]
        : body?.message;
    if (message) {
        return message;
    }
    return getRequestValidationMessage(body) ?? null;
}
